import { ApiError, type InstanceDetail } from "@keydials/api-client";

/**
 * What a dial admin who may NOT change the centre logo is told when they hover the hub (KEY-640, reworded for
 * KEY-718's policies): who to ask, which depends on why. Undefined when they may change it, or aren't an admin.
 */
export function centreLogoNote(
  detail: Pick<InstanceDetail, "logoOverrideAllowed" | "centralImageEditBlock" | "centralImageEditBlockedBy">,
  isDialAdmin: boolean,
): string | undefined {
  if (!isDialAdmin || detail.logoOverrideAllowed) return undefined;
  const by = detail.centralImageEditBlockedBy;
  switch (detail.centralImageEditBlock) {
    case "locked":
      return by
        ? `${by} has locked the logo on its dials, so it can't be changed here.`
        : "The logo on this dial is locked by your organisation.";
    case "managers-only":
      return by
        ? `${by} lets only organisation managers change dial logos. Ask one of them.`
        : "Only organisation managers can change this dial's logo. Ask one of them.";
    case "rebranding-off":
      return "This dial can't be rebranded.";
    default:
      // "not-switched-on", or an older payload that doesn't say.
      return "Changing the logo isn't switched on for this dial. Ask your Key Dials administrator to enable it.";
  }
}

/**
 * KEY-756: what a failed centre-logo upload tells the person. A 400 carries the server's own reason (too big,
 * wrong type, locked, managers only) and is shown as it is; 403 and 404 come back with empty bodies, so they
 * get wording here. Everything else keeps the status so a report of it can be traced.
 */
export function centreLogoUploadError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.status) {
      case 400:
        return e.message;
      case 403:
        return "Only this dial's admins can change its logo.";
      case 404:
        return "This dial couldn't be found in the organisation you're working in. Reload the page and try again.";
      case 413:
        return "That image is too large. Try one under 5 MB.";
      default:
        return e.message || `Could not upload the image (HTTP ${e.status}).`;
    }
  }
  // fetch rejects with a TypeError when the request never got an answer.
  return "Could not reach Key Dials to upload the image. Check your connection and try again.";
}
